import type {FC} from 'react';
import {Theme} from '@wellstone-solutions/common';
import {Box, Button, Icon, Typography} from '@wellstone-solutions/web';
import {toSignal} from '../../adapters';
import {formatReviewDate} from '../../relativeTime';
import {signalIconName} from '../../signalIcons';
import type {Indicator} from '../../types';

// Per-signal reviewed marker: a signal is reviewed once you've left feedback on
// it, so it replaces the feedback button. Styling follows the design's info chip
// (--pf-info / --pf-info-bg); the attribution line sits below the pill.
const ReviewedChip: FC<{submittedAt: string}> = ({submittedAt}) => (
  <Box sx={{mt: 1.25}}>
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 24,
        pl: '6px',
        pr: '8px',
        borderRadius: '999px',
        bgcolor: '#E1F5FE',
        color: '#0277BD',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
      <Icon name="DoubleCheck" size={16} />
      Reviewed
    </Box>
    <Typography
      sx={{fontSize: 12, color: 'text.secondary', lineHeight: 1.4, mt: 0.5}}>
      {formatReviewDate(submittedAt)} by you
    </Typography>
  </Box>
);

type SignalCardProps = {
  indicator: Indicator;
  // Shows a kind icon + label row above the headline (member-detail surfaces).
  // Omitted in the cross-member list, where the row already labels the kind.
  kindLabel?: string;
  // Absent while no feedback form is active (no form deployed)
  onGiveFeedback?: (indicatorId: string) => void;
};

// One signal: an optional kind row, headline + trend, and either the reviewed
// chip (once feedback exists) or the "Signal feedback" button that opens the V1
// feedback modal.
export const SignalCard: FC<SignalCardProps> = ({
  indicator,
  kindLabel,
  onGiveFeedback,
}) => {
  const feedback = indicator.myFeedback;
  return (
    <Box>
      {kindLabel && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 0.5,
          }}>
          <Icon
            name={signalIconName(indicator.kind)}
            size={18}
            color={Theme.colorPalette.primary}
          />
          <Typography sx={{fontSize: 14, fontWeight: 600}}>
            {kindLabel}
          </Typography>
        </Box>
      )}
      <Typography sx={{fontSize: 14, fontWeight: 600, lineHeight: 1.35}}>
        {indicator.headline}
      </Typography>
      <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 0.25}}>
        {toSignal(indicator).trend.text}
      </Typography>
      {feedback ? (
        <ReviewedChip submittedAt={feedback.submittedAt} />
      ) : (
        onGiveFeedback && (
          <Button
            size="small"
            variant="outlined"
            title="Tell us how this signal landed"
            startIcon={<Icon name="AntennaSignal" size={18} />}
            onClick={() => onGiveFeedback(indicator.id)}
            sx={{mt: 1.25, textTransform: 'none'}}>
            Signal feedback
          </Button>
        )
      )}
    </Box>
  );
};
